// Lightweight actuals tracking — one flat list per project.
// Category-scoped so entries roll up cleanly to the same buckets as the plan.

export const ACTUAL_CATEGORIES = [
  { value: "purchase",   label: "Purchase" },
  { value: "interior",   label: "Interior" },
  { value: "extensions", label: "Extensions" },
  { value: "exterior",   label: "Exterior" },
  { value: "systems",    label: "Systems" },
  { value: "soft",       label: "Soft Costs" },
  { value: "other",      label: "Other" },
];

// Map a dashboard tab id to the actuals category that tab represents.
export const TAB_TO_CATEGORY = {
  rooms: "interior",
  extensions: "extensions",
  exterior: "exterior",
  systems: "systems",
  soft: "soft",
};

export const CATEGORY_LABEL = Object.fromEntries(
  ACTUAL_CATEGORIES.map((c) => [c.value, c.label])
);

export function actualsTotal(actuals = []) {
  return actuals.reduce((s, a) => s + (Number(a.amount) || 0), 0);
}

export function actualsByCategory(actuals = []) {
  const out = { purchase: 0, interior: 0, extensions: 0, exterior: 0, systems: 0, soft: 0, other: 0 };
  for (const a of actuals) {
    const k = a.category || "other";
    out[k] = (out[k] || 0) + (Number(a.amount) || 0);
  }
  return out;
}

export function filterActuals(actuals = [], category) {
  if (!category) return actuals;
  return actuals.filter((a) => (a.category || "other") === category);
}

export function newActualId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "act-" + Math.random().toString(36).slice(2);
}

export function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export const ACTUALS_ENABLED =
  (process.env.REACT_APP_ENABLE_ACTUALS || "").toString() === "1";

// ---------- Ref-id helpers ----------
// ref_id shape examples:
//   room:<room_id>:item:<item_id>
//   room:<room_id>:general
//   cat:<category_key>:variant:<variant_id>
//   cat:<category_key>:general
//   soft:<soft_item_id>
//   ext:<ext_id>:general
//   ext:<ext_id>:shell:<item_id>
//   ext:<ext_id>:room:<room_id>:item:<item_id>
//   ext:<ext_id>:room:<room_id>:general
// Empty or missing ref_id means unallocated (still rolls up by category).

export function parseRefId(refId) {
  if (!refId || typeof refId !== "string") return null;
  const parts = refId.split(":");
  if (parts[0] === "room") {
    return {
      kind: "room",
      roomId: parts[1] || null,
      itemId: parts[2] === "item" ? parts[3] || null : null,
      general: parts[2] === "general",
    };
  }
  if (parts[0] === "cat") {
    return {
      kind: "cat",
      categoryKey: parts[1] || null,
      variantId: parts[2] === "variant" ? parts[3] || null : null,
      general: parts[2] === "general",
    };
  }
  if (parts[0] === "soft") {
    return { kind: "soft", softId: parts[1] || null };
  }
  if (parts[0] === "ext") {
    const out = { kind: "ext", extId: parts[1] || null, scope: null, roomId: null, itemId: null, general: false };
    if (parts[2] === "general") {
      out.general = true;
    } else if (parts[2] === "shell") {
      out.scope = "shell";
      out.itemId = parts[3] || null;
    } else if (parts[2] === "room") {
      out.scope = "room";
      out.roomId = parts[3] || null;
      if (parts[4] === "item") out.itemId = parts[5] || null;
      else if (parts[4] === "general") out.general = true;
    }
    return out;
  }
  return null;
}

export function buildRefId(kind, payload) {
  if (kind === "room") {
    const { roomId, itemId } = payload;
    if (!roomId) return "";
    return itemId ? `room:${roomId}:item:${itemId}` : `room:${roomId}:general`;
  }
  if (kind === "cat") {
    const { categoryKey, variantId } = payload;
    if (!categoryKey) return "";
    return variantId
      ? `cat:${categoryKey}:variant:${variantId}`
      : `cat:${categoryKey}:general`;
  }
  if (kind === "soft") return `soft:${payload.softId}`;
  if (kind === "ext") {
    const { extId, scope, itemId, roomId } = payload;
    if (!extId) return "";
    if (!scope) return `ext:${extId}:general`;
    if (scope === "shell") {
      return itemId ? `ext:${extId}:shell:${itemId}` : `ext:${extId}:general`;
    }
    if (scope === "room") {
      if (!roomId) return `ext:${extId}:general`;
      return itemId
        ? `ext:${extId}:room:${roomId}:item:${itemId}`
        : `ext:${extId}:room:${roomId}:general`;
    }
  }
  return "";
}

// Resolve a ref_id to a human label ("Master Bathroom · Tiling")
export function refLabel(refId, project) {
  const ref = parseRefId(refId);
  if (!ref || !project) return null;
  if (ref.kind === "room") {
    const room = (project.rooms || []).find((r) => r.id === ref.roomId);
    if (!room) return null;
    const roomName = room.name || room.room_type || "Room";
    if (ref.general) return `${roomName} · General`;
    const item = (room.work_items || []).find((w) => w.id === ref.itemId);
    return item ? `${roomName} · ${item.name}` : `${roomName}`;
  }
  if (ref.kind === "cat") {
    const all = [...(project.exterior || []), ...(project.systems || [])];
    const cat = all.find((i) => i.category === ref.categoryKey);
    const catLabel = cat?.label || humanise(ref.categoryKey);
    if (ref.general) return `${catLabel} · General`;
    const variant =
      cat && Array.isArray(cat.variants)
        ? cat.variants.find((v) => v.id === ref.variantId)
        : null;
    return variant ? `${catLabel} · ${variant.label}` : catLabel;
  }
  if (ref.kind === "soft") {
    const s = (project.soft_costs || []).find((x) => x.id === ref.softId);
    if (!s) return null;
    const lbl = s.label || s.key || "Soft cost";
    return `Soft · ${lbl}`;
  }
  if (ref.kind === "ext") {
    const ext = (project.extensions || []).find((e) => e.id === ref.extId);
    if (!ext) return null;
    const extName = ext.name || "Extension";
    if (ref.general) return `${extName} · General`;
    if (ref.scope === "shell") {
      const item = (ext.shell_items || []).find((it) => it.id === ref.itemId);
      return item ? `${extName} · Shell · ${item.name}` : `${extName} · Shell`;
    }
    if (ref.scope === "room") {
      const room = (ext.fitout_rooms || []).find((r) => r.id === ref.roomId);
      if (!room) return `${extName}`;
      const roomName = room.name || room.room_type || "Room";
      if (ref.general) return `${extName} · ${roomName} · General`;
      const item = (room.work_items || []).find((w) => w.id === ref.itemId);
      return item
        ? `${extName} · ${roomName} · ${item.name}`
        : `${extName} · ${roomName}`;
    }
  }
  return null;
}

function humanise(k = "") {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
